import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Charts in the briefing (server/briefingVisuals.js). The Owl chooses them itself
// — after writing the briefing it picks the charts/tables it was written from that
// best show its points — so there is nothing to pick here, only whether to show
// them and how many. One component, two surfaces: `scope` 'admin' = Howler staff
// on the client's behalf (Admin → the client → Briefing); 'my' = the client
// (⚙ Tune on home, writes need the digests permission).
export default function BriefingVisualsSettings({ scope = 'my', entityId, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    let alive = true;
    setCfg(null); setError('');
    api.briefingVisuals(scope, entityId)
      .then((r) => { if (alive) (r?.error ? setError(r.error) : setCfg(r)); })
      .catch(() => alive && setError('Could not load this setting.'));
    return () => { alive = false; };
  }, [scope, entityId]);

  if (error && !cfg) return <p style={hint}>{error}</p>;
  if (!cfg) return <p style={hint}>Loading…</p>;
  const canEdit = !!cfg.canManage;

  const save = async (patch) => {
    const prev = cfg;
    setCfg({ ...cfg, ...patch }); setBusy(true); setError('');
    try {
      const r = await api.saveBriefingVisuals(scope, entityId, patch);
      if (r?.error) { setCfg(prev); setError(r.error); return; }
      setCfg(r); setSavedAt(Date.now()); onSaved?.();
    } catch { setCfg(prev); setError('Could not save — try again.'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <p style={hint}>
        The Owl adds charts under the briefing to show the points it makes — the daily sales chart when it talks about pace, the ticket-type
        breakdown when it talks about the mix. It picks them itself, fresh each time, from the {scope === 'admin' ? 'client’s' : 'your'} own dashboard
        tiles, with the same event filters. Changes apply the next time the briefing is refreshed.
      </p>
      {!cfg.flagOn && (
        <p style={{ ...hint, color: '#b45309' }}>
          {scope === 'admin'
            ? 'Switched off for this client — turn on “Charts & tables in the briefing” in Admin → Product → Flags for these to show.'
            : 'Charts in the briefing aren’t switched on for your account yet — ask Howler.'}
        </p>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40, fontSize: 13.5, fontWeight: 600, cursor: canEdit ? 'pointer' : 'default' }}>
        <input type="checkbox" checked={!!cfg.enabled} disabled={!canEdit || busy} onChange={(e) => save({ enabled: e.target.checked })} style={{ width: 20, height: 20 }} />
        Show charts in the briefing
      </label>
      {cfg.enabled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--muted-2)' }}>Up to</span>
          {Array.from({ length: cfg.maxCap || 4 }, (_, i) => i + 1).map((n) => (
            <button key={n} onClick={() => save({ max: n })} disabled={!canEdit || busy} style={{ ...chip, ...(cfg.max === n ? chipOn : null) }} aria-pressed={cfg.max === n}>{n}</button>
          ))}
          <span style={{ fontSize: 13, color: 'var(--muted-2)' }}>{cfg.max === 1 ? 'chart' : 'charts'} — the Owl may show fewer when fewer fit</span>
        </div>
      )}
      <div style={{ minHeight: 18, marginTop: 6, fontSize: 12 }}>
        {error ? <span style={{ color: 'var(--error)' }}>{error}</span>
          : savedAt > 0 ? <span style={{ color: 'var(--muted)' }}>Saved ✓</span>
          : !canEdit ? <span style={{ color: 'var(--muted)' }}>Only people who can manage briefings &amp; digests can change this.</span> : null}
      </div>
    </div>
  );
}

const hint = { fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55, margin: '0 0 10px' };
const chip = { minWidth: 40, minHeight: 40, border: '1.5px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 980, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const chipOn = { borderColor: 'var(--brand)', background: 'var(--brand)', color: '#fff' };
